import React from 'react';

import _, { isEmpty } from 'lodash';
import { toDollars } from '../../Purchase/utils';

import {
  queryAdminApplyPayment,
  queryGetPaymentApplicationMonths,
  queryGetFacilitySettings,
  queryGetInvoice,
  queryGetFacilityPaymentTypes,
} from '../../../Queries';

import Field from '../../Fields/Field';
import InputCurrencyField from '../../Fields/InputCurrencyField';
import InputSelectField from '../../Fields/InputSelectField';
import InputTextField from '../../Fields/InputTextField';

import InvoiceOverpaymentCreditTable from "../../Fields/InvoiceOverpaymentCreditTable";
import InvoicePaymentLineItemEntryTable from '../../Fields/InvoicePaymentLineItemEntryTable';

import ActionButton from '../../Common/ActionButton';
import PanelBlock from '../../Common/PanelBlock';
import ProcessingIndicator from '../../Common/ProcessingIndicator';

import Invoice from '../../../Entities/Invoice';
import PaymentApplicationMonth from '../../../Entities/PaymentApplicationMonth';
import { invoiceQueryFields } from './InvoicePanel';

import moment from 'moment';
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import InvoicePaymentPanelConfig from './Config/InvoicePaymentPanelConfig';
import './InvoicePaymentPanel.scss';

import featureFlags from '../../../featureFlags';

class InvoicePaymentPanel extends React.Component {
  state = {
    initializationComplete: false,
    initializationErrors: [],
    payment: null,
    paymentAllocation: null,
    paymentEntries: {},
    paymentSaveInProgress: false,
    applicationDate: null,
    applicationType: null,
    paymentType: null,
    overPayment: null,
    overpaymentCredit: null,
    isConfirmRequired: false,
    isConfirmInitiated: false,
    paymentApplicationMonths: [],
    futureMonthApplication: "",
    accountingMethod: null,
    facilityPaymentTypes: [],
  };

  get fieldConfiguration() {
    return this.config.fieldConfiguration;
  }

  get credits() {
    return this.config.credits;
  }

  get noAppDateTypes() {
    return ['APPLY_CASH', 'CARRYOVER_CREDIT', 'DISCOUNT_CREDIT'];
  }

  async componentDidMount() {
    try {
      const invoicePanelConfig = new InvoicePaymentPanelConfig({ invoice: this.props.invoice });
      const config = await invoicePanelConfig.loadConfig();
      this.config = config;

      this.setState({
        ...this.state,
        ...config.initialState,
        applicationDate: new Date(),
      });

      const applicationMonthsQuery = [
        'month',
        'year',
        'status',
      ];

      const monthsResponse = await queryGetPaymentApplicationMonths({
        facility_id: this.props.facilityId
      }, applicationMonthsQuery);

      const responseMonths = monthsResponse.data.getPaymentApplicationMonths;
      const paymentApplicationMonths = responseMonths.map(month => PaymentApplicationMonth.createFromQueryValues(month));

      this.setState({ paymentApplicationMonths });

      const facilitySettingsFields = ['future_month_application', 'accounting_method'];

      const settingsResponse = await queryGetFacilitySettings({
        facility_id: this.props.facilityId
      }, facilitySettingsFields);

      const facilityPaymentTypeResponse = await queryGetFacilityPaymentTypes(
        { facilityId: this.props.facilityId },
        [{ 'paymentTypes': ['paymentTypeName'] }],
      )
      const facilityPaymentTypes = facilityPaymentTypeResponse.data.getFacilityPaymentTypes?.paymentTypes?.map(({ paymentTypeName }) => paymentTypeName);

      const facilitySettings = settingsResponse.data.getFacilitySettings;
      const futureMonthApplication = facilitySettings?.future_month_application;
      const facilityAccountingMethod = facilitySettings.accounting_method;



      this.setState({
        facilitySettings,
        futureMonthApplication,
        facilityPaymentTypes,
        accountingMethod: facilityAccountingMethod,
        initializationComplete: true,
      });
    } catch (errors) {
      this.setState({
        initializationComplete: true,
        initializationErrors: errors
      });
    }
  }

  componentDidUpdate() {
    const {
      payment,
      paymentAllocation,
      overPayment,
      isConfirmRequired,
      isConfirmInitiated,
    } = this.state;

    const {
      invoice,
    } = this.props;

    if (!payment || !invoice || !featureFlags.OVERPAYMENT) return;

    const balance = invoice.fields.balance.value;
    const paymentType = payment.fields.type.value;
    const paymentAmount = payment.fields.amount.value;

    const updatedOverPayment = (paymentAmount - balance) > 0 ? (paymentAmount - balance) : 0;
    let updatedIsConfirmRequired = isConfirmRequired;
    let updatedIsConfirmInitiated = isConfirmInitiated;
    let updatedPaymentAllocation = paymentAllocation;
    let isUpdateState = false;

    //update overpayment UI
    const isUpdateOverpaymentUI = updatedOverPayment !== overPayment ||
      (paymentType === 'CREDIT' && isConfirmRequired);
    if (isUpdateOverpaymentUI) {
      updatedIsConfirmRequired = updatedOverPayment > 0 && paymentType !== 'CREDIT';
      if (updatedOverPayment === 0 || !isConfirmRequired) {
        updatedIsConfirmInitiated = false;
      }
      isUpdateState = true;
    }

    //update allocation
    if (paymentAllocation === 'FULL' && paymentAmount < balance) {
      updatedPaymentAllocation = null;
      isUpdateState = true;
    } else if (paymentAllocation !== 'FULL' && (paymentAmount !== 0 && paymentAmount >= balance && paymentType !== 'CREDIT')) {
      updatedPaymentAllocation = 'FULL';
      isUpdateState = true;
    }

    //update state
    if (isUpdateState) {
      this.setState({
        overPayment: updatedOverPayment,
        isConfirmRequired: updatedIsConfirmRequired,
        isConfirmInitiated: updatedIsConfirmInitiated,
        paymentAllocation: updatedPaymentAllocation,
      });
    }
  }

  get paymentAllocationTypeOptions() {
    const isFullOptionDisabled = (this.state.payment.fields.amount.value < this.props.invoice.fields.balance.value)
    const options = this.fieldConfiguration.allocation.options.map(option => {
      return (option.value === 'FULL') ?
        { ...option, disabled: isFullOptionDisabled } :
        option;
    });
    return options;
  }

  isCreditDisabled() {
    const { invoice } = this.props;

    return this.credits.length === 0
      || !invoice
      || !invoice.fields.balance.value;
  }

  getCreditOption() {
    return {
      value: 'CREDIT',
      label: 'Credit',
      disabled: this.isCreditDisabled()
    }
  }

  get paymentTypeOptions() {
    const facilityPaymentTypesOptions = this.state.facilityPaymentTypes?.map((paymentType) => {
      return {
        value: paymentType,
        label: paymentType,
      }
    });

    return [
      ...[{ value: null, label: '' }],
      ...(facilityPaymentTypesOptions ? facilityPaymentTypesOptions : []),
      ...[this.getCreditOption()],
    ]
  }

  savePayment() {
    return new Promise((resolve, reject) => {
      const data = this.state.payment.getAdminApplyPaymentQueryValues(
        this.props.invoice.fields.id.value,
        this.state.paymentAllocation,
        this.state.paymentEntries,
        this.state.overpaymentCredit
      );

      const appDateFormatted = moment(this.state.applicationDate).format('YYYY-MM-DD');

      if (featureFlags.APPLICATION_DATE) {
        data.application_date = featureFlags.OVERPAYMENT_PT2_I
          && (this.noAppDateTypes.includes(this.state.applicationType)) ? null : appDateFormatted;
      }
      data.payment_type = featureFlags.OVERPAYMENT_PT2_C ? null : 'Check';
      const overpaymentCredit = data.overpayment_credit
      if (overpaymentCredit?.application_type === "APPLY_CASH") {
        data.overpayment_credit = {
          ...overpaymentCredit,
          amount: overpaymentCredit.amount ?? 0,
          amount_tax_1: overpaymentCredit.amount_tax_1 ?? 0,
          amount_tax_2: overpaymentCredit.amount_tax_2 ?? 0,
          amount_tax_3: overpaymentCredit.amount_tax_3 ?? 0,
          amount_tax_4: overpaymentCredit.amount_tax_4 ?? 0
        }
      }
      queryAdminApplyPayment(data).then(async response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const invoice = await this.fetchInvoice();
        resolve(invoice);
      }, error => {
        reject([error]);
      });
    });
  }


  _storePaymentFormData(attribute) {
    return value => {
      this.setState({
        [attribute]: value
      });
    };
  }


  _storePaymentData(inputId) {
    return value => {
      const payment = _.cloneDeep(this.state.payment);
      payment.fields[inputId].value = value;

      if (inputId === 'credit_id') {
        const credit = this.credits.find(credit => credit.fields.id.value === value);
        if (featureFlags.OVERPAYMENT_PT2_I) {
          if (this.noAppDateTypes.includes(credit.fields.application_type.value)) {
            this.setState({ applicationDate: null });
          } else {
            this.setState({ applicationDate: moment().toDate() });
          }
        }
        this.setState({ applicationType: credit.fields.application_type.value })
        this.setState({ paymentType: credit.fields.payment_type.value })
        payment.fields.amount.value = credit.fields.balance.value;
        payment.fields.payment_meta.value = credit.fields.payment_meta.value;
      } else if (inputId === 'type' && value === 'Check') {
        this.setState({ applicationDate: new Date() })
        payment.fields.amount.value = null;
        payment.fields.payment_meta.value = null;
      }

      this.setState({ payment });
    };
  }


  _storePaymentMetaData(attribute) {
    return value => {
      let paymentMeta = _.cloneDeep(this.state.payment.fields.payment_meta.value);
      paymentMeta = {
        ...paymentMeta,
        [attribute]: value
      };

      this._storePaymentData('payment_meta')(paymentMeta);
    };
  }


  onPaymentEntryChange = (ident, value) => {/**/
    const paymentEntries = {
      ...this.state.paymentEntries,
      [ident]: value
    };
    this.setState({ paymentEntries });

    const { payment, paymentAllocation } = this.state;

    if (payment.fields.type.value === 'CREDIT' && paymentAllocation === 'PARTIAL_CUSTOM') {
      payment.fields.amount.value = 0;
      for (const id in paymentEntries) {
        payment.fields.amount.value += paymentEntries[id].fields.amount.value;
        payment.fields.amount.value += paymentEntries[id].fields.amount_tax_1.value;
        payment.fields.amount.value += paymentEntries[id].fields.amount_tax_2.value;
        payment.fields.amount.value += paymentEntries[id].fields.amount_tax_3.value;
        payment.fields.amount.value += paymentEntries[id].fields.amount_tax_4.value;
      }
      this.setState({ payment });
    }
  }

  onOverpaymentCreditChange = valueObject => {
    const overpaymentCredit = valueObject ? {
      ...this.state.overpaymentCredit,
      ...valueObject
    } : null;
    if (overpaymentCredit && overpaymentCredit.application_date) {
      overpaymentCredit.application_date = moment(overpaymentCredit.application_date).format('YYYY-MM-DD');
    }
    this.setState({ overpaymentCredit });
  }

  handleApplyPaymentSaveClick = () => {
    const {
      isConfirmRequired,
      isConfirmInitiated,
      applicationDate,
      applicationType
    } = this.state;
    if (this.state.overpaymentCredit?.application_type === 'APPLY_CASH') {
      const lineItemFields = this.state.overpaymentCredit;
      const netAndTaxSum = lineItemFields.amount + lineItemFields.amount_tax_1 + lineItemFields.amount_tax_2 + lineItemFields.amount_tax_3 + lineItemFields.amount_tax_4;
      if (netAndTaxSum !== this.state.overPayment) {
        alert(["Net Amount and taxes must equal overpayment amount"]);
        return;
      }
    }
    if (featureFlags.OVERPAYMENT) {
      if (isConfirmRequired && !isConfirmInitiated) {
        this.setState({ isConfirmInitiated: true });
        return;
      }
    }

    if (applicationDate || this.noAppDateTypes.includes(applicationType)) {
      this.setState({
        paymentSaveInProgress: true
      }, () => {
        this.savePayment().then(invoice => {
          this.setState({
            paymentSaveInProgress: false,
            isConfirmInitiated: false,
          }, () => {
            this.props.onSave(invoice);
          });
        }, errors => {
          let errorMessage = 'An error occurred while saving invoice payment:\n\n';
          for (let error of errors) {
            errorMessage += error + '\n';
          }
          alert(errorMessage);

          this.setState({
            paymentSaveInProgress: false
          });
        });
      });
    }
  }

  fetchInvoice = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const invoiceId = urlSearchParams.get('invoice');

    const response = await queryGetInvoice({ invoice_id: invoiceId }, invoiceQueryFields);
    if (response.errors) {
      throw response.errors.map(error => error.message);
    }

    const invoice = Invoice.createFromQueryValues(response.data.getInvoice);
    return invoice;
  }

  handleApplyPaymentCancelClick = () => {
    if (this.state.isConfirmInitiated) {
      this.setState({ isConfirmInitiated: false });
    }
    else {
      this.props.onCancel();
    }
  }


  renderInitializationMessage() {
    const {
      initializationComplete,
      initializationErrors
    } = this.state;

    if (!initializationComplete) {
      return (
        <div className="parkchirp--page--user-message-container">
          <ProcessingIndicator userMessage="Initializing payment, please wait..." />
        </div>
      );
    }

    if (initializationErrors.length) {
      return (
        <div className="parkchirp--page--user-message-container">
          <div className="parkchirp--page--user-message">
            <p>The following errors occurred while initializing the payment:</p>
            <ul>
              {initializationErrors.map(error =>
                <li key={performance.now()}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      );
    }
  }

  getPastDatesToExclude = () => {
    const { paymentApplicationMonths } = this.state;

    if (!paymentApplicationMonths.length) return [];

    const closedMonths = paymentApplicationMonths.filter(month => month.fields.status.value === "CLOSED");

    // Exclude every day of every closed month
    const datesToExclude = closedMonths.map(closedMonth => {
      const year = closedMonth.fields.year.value;

      let month = closedMonth.fields.month.value.toLowerCase();
      month = month.charAt(0).toUpperCase() + month.slice(1);
      month = moment(`${month}, ${year}`, 'MMM YYYY');

      const daysInMonth = month.daysInMonth();
      return [...new Array(daysInMonth)].map((_, i) => {
        return new Date(month.date(i + 1));
      });
    }).flat();

    return datesToExclude;
  };

  isDisableAllocationField() {
    if (!featureFlags.OVERPAYMENT) {
      return false;
    }

    const {
      payment,
      overPayment,
      isConfirmInitiated
    } = this.state;

    const paymentType = payment ? payment.fields.type.value : "";
    return isConfirmInitiated || (overPayment > 0 && paymentType !== 'CREDIT');
  }

  isDisabledDateField() {
    const { payment, applicationType } = this.state;
    if (applicationType === 'CARRYOVER_CREDIT' || applicationType === 'DISCOUNT_CREDIT') return true;
    if (payment.fields.type.value === 'Check') return false;
    if (featureFlags.OVERPAYMENT_PT2_I && applicationType !== 'APPLY_CASH') return false;
    return true;
  }

  renderPayment() {
    const {
      payment,
      paymentAllocation,
      paymentEntries,
      applicationDate,
      applicationType,
      overPayment,
      futureMonthApplication,
      isConfirmRequired,
      isConfirmInitiated,
      accountingMethod
    } = this.state;

    const {
      invoice,
      editMode
    } = this.props;

    const paymentLineItemEntryTableRows = invoice.fields.line_items.value.map(lineItem => {
      return {
        ident: lineItem.fields.id.value,
        invoiceLineItem: lineItem,
        paymentEntry: paymentEntries[lineItem.fields.id.value]
      };
    });

    let isShowOverPaymentMessage = overPayment != null && overPayment > 0 && isConfirmRequired;

    if (!featureFlags.OVERPAYMENT) {
      isShowOverPaymentMessage = false;
    }

    const datesToExclude = this.getPastDatesToExclude();

    const startDate = new Date(moment().subtract(1, "year").startOf("month").add(1, "day").format("YYYY-MM-DD"));
    const tomorrow = new Date(moment().add(1, "day").format('YYYY-MM-DD'));
    const currentMonth = new Date(moment().startOf("month").add(1, "month").format('YYYY-MM-DD'));

    const maxDate = isEmpty(datesToExclude) ? tomorrow :
      futureMonthApplication === "NOT_ALLOWED" ? tomorrow :
        futureMonthApplication === "ALLOWED_CURRENT_MONTH" ? currentMonth : "";

    const dateConfig = {
      exclude: datesToExclude,
      minDate: startDate,
      maxDate
    };

    return (
      <React.Fragment>
        <div className="parkchirp--invoice-payment-panel--main-inputs-container">
          <Field label="Type"
            id="parkchirp--payment--type"
            containerClasses={['parkchirp--payment-input-block']}
            layout="stacked">
            <InputSelectField inputId="parkchirp--payment--type"
              options={this.paymentTypeOptions}
              value={payment.fields.type.value}
              onChange={this._storePaymentData('type')}
              editMode={editMode}
              disabledMode={isConfirmInitiated}
            />
          </Field>
          {(![null, 'CREDIT'].includes(payment.fields.type.value)) && (
            <>
              <Field label="Amount"
                id="parkchirp--payment--amount"
                containerClasses={['parkchirp--payment-input-block']}
                layout="stacked">
                <InputCurrencyField inputId="parkchirp--payment--amount"
                  value={payment.fields.amount.value}
                  onChange={this._storePaymentData('amount')}
                  editMode={editMode}
                  disabledMode={isConfirmInitiated}
                />
              </Field>
              <Field label="Payment Reference"
                id="parkchirp--payment-meta--check-number"
                containerClasses={['parkchirp--payment-input-block']}
                layout="stacked">
                <InputTextField inputId="parkchirp--payment-meta--check-number"
                  value={payment.fields.payment_meta.value?.check_number || null}
                  onChange={this._storePaymentMetaData('check_number')}
                  editMode={editMode}
                  disabledMode={isConfirmInitiated}
                />
              </Field>
            </>
          )}
          {payment.fields.type.value === 'CREDIT' && (
            <Field label="Credit Number"
              id="parkchirp--payment-meta--credit-number"
              containerClasses={['parkchirp--payment-input-block']}
              layout="stacked">
              <InputSelectField inputId="parkchirp--payment-meta--credit-number"
                value={payment.fields.payment_meta.value?.credit_id}
                options={this.fieldConfiguration.credit_id.options}
                onChange={this._storePaymentData('credit_id')}
                editMode={editMode}
              />
            </Field>
          )}
          <Field label="Allocation"
            id="parkchirp--payment--allocation-type"
            containerClasses={['parkchirp--payment-input-block']}
            layout="stacked">
            <InputSelectField inputId="parkchirp--payment--allocation-type"
              options={this.paymentAllocationTypeOptions}
              value={paymentAllocation}
              onChange={this._storePaymentFormData('paymentAllocation')}
              editMode={editMode}
              disabledMode={this.isDisableAllocationField()}
            />
          </Field>
          {featureFlags.APPLICATION_DATE && payment.fields.type.value &&
            <Field label="Application Date"
              id="parkchirp--payment--application-date"
              containerClasses={['parkchirp--payment-input-block']}
              layout="stacked">
              <DatePicker
                id={`parkchirp--payment--application-date-field`}
                selected={applicationDate}
                onChange={date => this.setState({ applicationDate: date })}
                excludeDates={datesToExclude}
                minDate={startDate}
                maxDate={maxDate}
                disabled={this.isDisabledDateField()}
                placeholderText={'-'}
              />
              {
                !applicationDate
                && (
                  featureFlags.OVERPAYMENT_PT2_I &&
                  !this.noAppDateTypes.includes(applicationType)
                )
                && <span className='error'>Provide a valid date</span>
              }
            </Field>
          }

          {isShowOverPaymentMessage &&
            <div className='parkchirp--invoice-payment-panel--check-overpayment-message'>
              <strong>Overpayment</strong>
              <div>
                {toDollars(overPayment)}
              </div>
            </div>
          }

        </div>
        {(paymentAllocation === 'PARTIAL_CUSTOM') &&
          <div className="parkchirp--invoice-payment-panel--line-item-entries-container">
            <InvoicePaymentLineItemEntryTable inputId="parkchirp--payment--entries"
              rows={paymentLineItemEntryTableRows}
              fieldConfiguration={this.fieldConfiguration}
              onEntryChange={this.onPaymentEntryChange}
              editMode={editMode}
            />
          </div>
        }
        {featureFlags.OVERPAYMENT_PT2_I && isShowOverPaymentMessage &&
          <div className="parkchirp--invoice-payment-panel--line-item-entries-container">
            <InvoiceOverpaymentCreditTable
              applicationTypes={this.fieldConfiguration.application_type}
              accountingMethod={accountingMethod}
              paymentApplicationDate={applicationDate}
              dateConfig={dateConfig}
              onChange={this.onOverpaymentCreditChange}
              isConfirmInitiated={isConfirmInitiated}
              overPaymentAmount={overPayment}
            />
          </div>
        }
      </React.Fragment>
    );
  }


  render() {
    const {
      initializationComplete,
      initializationErrors,
      paymentSaveInProgress,
      overPayment,
      isConfirmRequired,
      isConfirmInitiated,
      overpaymentCredit
    } = this.state;

    const {
      panelHeading
    } = this.props;

    let disableSaveInvoice = false;

    if (overpaymentCredit) {

      disableSaveInvoice = (overpaymentCredit.amount && overpaymentCredit.amount < 0) ||
        (overpaymentCredit.amount_tax_1 && overpaymentCredit.amount_tax_1 < 0) ||
        (overpaymentCredit.amount_tax_2 && overpaymentCredit.amount_tax_2 < 0) ||
        (overpaymentCredit.amount_tax_3 && overpaymentCredit.amount_tax_3 < 0) ||
        (overpaymentCredit.amount_tax_4 && overpaymentCredit.amount_tax_4 < 0) ? true : false;
    }

    let postHeadingText = isConfirmInitiated ? "A Credit of " + toDollars(overPayment) + " will be created.  Select Confirm to proceed or Cancel" : "";
    let saveButtonText = isConfirmRequired && isConfirmInitiated ? "Confirm" : "Save";


    if (!featureFlags.OVERPAYMENT) {
      postHeadingText = "";
      saveButtonText = "Save";
    }

    return (
      <div className="parkchirp--invoice-payment-panel">
        <PanelBlock
          heading={panelHeading}
          postHeading={postHeadingText}
          actions={[
            <ActionButton label={saveButtonText}
              inProgressState={paymentSaveInProgress}
              onClick={this.handleApplyPaymentSaveClick}
              disabled={disableSaveInvoice}
            />,
            <button type="button"
              className="parkchirp--button-as-link"
              style={{ paddingLeft: '10px', paddingRight: '10px' }}
              onClick={this.handleApplyPaymentCancelClick}>
              Cancel
            </button>
          ]}>
          {(!initializationComplete || initializationErrors.length > 0) &&
            this.renderInitializationMessage()
          }
          {(initializationComplete && initializationErrors.length === 0) &&
            this.renderPayment()
          }
        </PanelBlock>
      </div>
    );
  }
}
export default InvoicePaymentPanel;
